const FAT_FRACTION_MIN = 0.2
const FAT_FRACTION_MAX = 0.9
const TAU_HOURS = 16
const KCAL_PER_GRAM_FAT = 9.4
const MIN_INTERVAL_MS = 60_000
const SECONDS_TO_FAT_BURN_TRESHOLD = 8 * 3600

const cache = new Map<string, { ts: number; value: number }>()

/**
 * Computes cumulative grams of fat oxidized since a fast started, using an exponential model.
 *
 * @param secondsSinceStart - Seconds elapsed since the fast began.
 * @param dailyKcal - Assumed daily kcal expenditure.
 * @returns Cumulative fat grams, or NaN for invalid input.
 */
function computeCumulativeFatGrams(secondsSinceStart: number, dailyKcal: number) {
   if (!Number.isFinite(secondsSinceStart) || !Number.isFinite(dailyKcal)) {
      return NaN
   }
   const tHours = Math.max(0, secondsSinceStart) / 3600
   const kcalPerHour = dailyKcal / 24
   const fMin = FAT_FRACTION_MIN
   const fMax = FAT_FRACTION_MAX
   const tau = TAU_HOURS
   const expTerm = Math.exp(-tHours / tau)
   const cumulativeFatKcal =
      kcalPerHour * (fMin * tHours + (fMax - fMin) * (tHours - tau * (1 - expTerm)))
   return cumulativeFatKcal / KCAL_PER_GRAM_FAT
}

/**
 * Returns grams of fat burned past the fat-burning threshold, memoized briefly per daily-kcal value.
 *
 * @param secondsSinceStart - Seconds elapsed since the fast began.
 * @param dailyKcal - Assumed daily kcal expenditure.
 * @param options - Set `skipCache` to bypass the short-lived cache.
 * @returns Grams of fat burned beyond the threshold (0 if none).
 */
export function getCumulativeFatBurned(
   secondsSinceStart: number,
   dailyKcal: number,
   options: { skipCache?: boolean } = {},
) {
   const skipCache = options.skipCache === true
   const key = String(dailyKcal)
   const now = Date.now()
   if (!skipCache) {
      const entry = cache.get(key)
      if (entry && now - entry.ts < MIN_INTERVAL_MS) {
         return entry.value
      }
   }
   const preFastingFatBurn = computeCumulativeFatGrams(
      SECONDS_TO_FAT_BURN_TRESHOLD,
      dailyKcal,
   )
   const value =
      computeCumulativeFatGrams(secondsSinceStart, dailyKcal) - preFastingFatBurn
   if (!skipCache && value > 0) {
      cache.set(key, { ts: now, value })
   }
   return value > 0 ? value : 0
}

/**
 * Formats grams of fat as a display string with adaptive precision.
 *
 * @param grams - The grams value.
 * @returns The formatted string, or `--` when not finite.
 */
export function formatFatGrams(grams: number) {
   if (!Number.isFinite(grams)) {
      return '--'
   }
   if (grams < 100) {
      return `${grams.toFixed(3)} g`
   }
   if (grams < 1000) {
      return `${grams.toFixed(2)} g`
   }

   const totalGrams = Math.max(0, Math.floor(grams))
   const kilograms = Math.floor(totalGrams / 1000)
   const remainingGrams = totalGrams % 1000
   const parts: string[] = []
   if (kilograms > 0) {
      parts.push(`${kilograms} kg`)
   }
   if (remainingGrams > 0) {
      parts.push(`${remainingGrams} g`)
   }
   if (parts.length === 0) {
      return `${grams.toFixed(2)} g`
   }
   return parts.join(' ')
}
